import React, { useState, useEffect } from 'react';
import { User, Building, Edit3, History, Sparkles, FileText, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatErrorMessage } from '../utils/api';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

/**
 * Semáforo de asistencia para una tarjeta de "Reunión Socios".
 *
 * - 👤 Indicador Cliente: Gris (pendiente) / Verde (confirmado) /
 *      Rojo (cancelado o reagendar_solicitado)
 * - 🏢 Indicador Peritum (consolidado del equipo interno):
 *      Gris  → nadie ha confirmado
 *      Amarillo → algunos confirmaron, otros pendientes
 *      Verde → todos confirmaron
 *      Rojo  → al menos un asistente canceló
 *
 * Incluye botón "Forzar Confirmación Manual" para que cualquier comercial
 * marque manualmente el estado del cliente o de un asistente interno.
 */
const colorCliente = (estado) => {
  if (estado === 'confirmado') return { dot: 'bg-emerald-500', label: 'Confirmado', text: 'text-emerald-700' };
  if (estado === 'cancelado') return { dot: 'bg-rose-500', label: 'Cancelado', text: 'text-rose-700' };
  if (estado === 'reagendar_solicitado') return { dot: 'bg-rose-400', label: 'Reagendar', text: 'text-rose-600' };
  return { dot: 'bg-slate-400', label: 'Pendiente', text: 'text-slate-500' };
};

const consolidarEquipo = (asistentes) => {
  if (!asistentes || asistentes.length === 0) {
    return { dot: 'bg-slate-300', label: 'Sin equipo', text: 'text-slate-500' };
  }
  const total = asistentes.length;
  const conf = asistentes.filter(a => a.estado === 'confirmado').length;
  const can = asistentes.filter(a => a.estado === 'cancelado').length;
  if (can > 0) return { dot: 'bg-rose-500', label: `${can} cancelado(s)`, text: 'text-rose-700' };
  if (conf === 0) return { dot: 'bg-slate-400', label: 'Nadie confirmó', text: 'text-slate-500' };
  if (conf === total) return { dot: 'bg-emerald-500', label: 'Todos confirmaron', text: 'text-emerald-700' };
  return { dot: 'bg-amber-400', label: `${conf}/${total} confirmados`, text: 'text-amber-700' };
};

export const ReunionSemaforo = ({ lead, onUpdated }) => {
  const reunion = lead.reunion_socios || {};
  const cli = colorCliente(reunion.cliente_estado || 'pendiente');
  const eq = consolidarEquipo(reunion.asistentes_internos);
  const [openForzar, setOpenForzar] = useState(false);
  const [openHistorial, setOpenHistorial] = useState(false);

  return (
    <div className='mt-2 pt-2 border-t border-indigo-100/70 space-y-1' data-testid={`semaforo-${lead.id}`}>
      <div className='flex items-center justify-between gap-2 text-[11px]'>
        <span className='inline-flex items-center gap-1.5'>
          <User className='w-3 h-3 text-slate-500' />
          <span className={`w-2 h-2 rounded-full ${cli.dot}`} />
          <span className={cli.text} data-testid={`semaforo-cliente-${lead.id}`}>{cli.label}</span>
        </span>
        <span className='inline-flex items-center gap-1.5'>
          <Building className='w-3 h-3 text-slate-500' />
          <span className={`w-2 h-2 rounded-full ${eq.dot}`} />
          <span className={eq.text} data-testid={`semaforo-equipo-${lead.id}`}>{eq.label}</span>
        </span>
      </div>

      <div className='flex items-center justify-between gap-2 mt-0.5'>
        <button
          onClick={(e) => { e.stopPropagation(); setOpenForzar(true); }}
          className='flex-1 text-[10px] text-indigo-600 hover:text-indigo-800 inline-flex items-center justify-center gap-1'
          data-testid={`forzar-confirmacion-btn-${lead.id}`}
        >
          <Edit3 className='w-3 h-3' />
          Forzar confirmación
        </button>
        <span className='text-slate-300'>·</span>
        <button
          onClick={(e) => { e.stopPropagation(); setOpenHistorial(true); }}
          className='flex-1 text-[10px] text-slate-500 hover:text-slate-800 inline-flex items-center justify-center gap-1'
          data-testid={`historial-btn-${lead.id}`}
        >
          <History className='w-3 h-3' />
          Historial
        </button>
      </div>

      {openForzar && (
        <ForzarConfirmacionDialog
          lead={lead}
          onClose={() => setOpenForzar(false)}
          onUpdated={onUpdated}
        />
      )}
      {openHistorial && (
        <HistorialAuditDialog
          lead={lead}
          onClose={() => setOpenHistorial(false)}
        />
      )}

      <TranscripcionPanel lead={lead} onUpdated={onUpdated} />
    </div>
  );
};

// ============================================================
// Panel de transcripción Meet + resumen Gemini
// ============================================================
const TranscripcionPanel = ({ lead, onUpdated }) => {
  const reunion = lead.reunion_socios || {};
  const meetingId = reunion.cerebro_meeting_id;
  const importedAt = reunion.transcript_imported_at;
  const summaryGemini = reunion.summary_gemini;
  const [importing, setImporting] = useState(false);
  const [openSummary, setOpenSummary] = useState(false);

  // No mostrar si la reunión aún no se clonó al Cerebro (cliente no confirmó)
  if (!meetingId) return null;

  const importarTranscript = async (e) => {
    e.stopPropagation();
    setImporting(true);
    try {
      const { data } = await api.post(`/meetings/${meetingId}/import-transcript`);
      toast.success(
        data.has_summary_gemini
          ? 'Transcripción + resumen Gemini importados'
          : 'Transcripción importada'
      );
      if (onUpdated) onUpdated();
    } catch (err) {
      toast.error(formatErrorMessage(err) || 'No se encontró transcripción en Drive');
    } finally {
      setImporting(false);
    }
  };

  if (importedAt) {
    return (
      <div className="mt-1.5 pt-1.5 border-t border-emerald-100/60 space-y-1" data-testid={`transcript-panel-${lead.id}`}>
        <div className="flex items-center gap-1.5 text-[10px]">
          <FileText className="w-3 h-3 text-emerald-600" />
          <span className="text-emerald-700 font-semibold">Transcripción importada</span>
          <span className="text-slate-400 ml-auto">
            {new Date(importedAt).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' })}
          </span>
        </div>
        {summaryGemini && (
          <button
            onClick={(e) => { e.stopPropagation(); setOpenSummary(true); }}
            className="w-full bg-gradient-to-r from-violet-50 to-indigo-50 hover:from-violet-100 hover:to-indigo-100 border border-violet-200 rounded px-2 py-1 transition-colors text-left"
            data-testid={`gemini-summary-btn-${lead.id}`}
          >
            <div className="flex items-center gap-1 text-[10px] text-violet-700 font-semibold mb-0.5">
              <Sparkles className="w-3 h-3" />
              Resumen Gemini Notes
            </div>
            <p className="text-[10px] text-slate-700 line-clamp-2 leading-snug">
              {summaryGemini.slice(0, 180)}{summaryGemini.length > 180 ? '…' : ''}
            </p>
          </button>
        )}
        {openSummary && (
          <Dialog open={true} onOpenChange={(o) => !o && setOpenSummary(false)}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid={`gemini-summary-modal-${lead.id}`}>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-violet-600" />
                  Resumen Gemini · {reunion.cliente_nombre || lead.nombre}
                </DialogTitle>
              </DialogHeader>
              <div className="bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200 rounded-lg p-4">
                <pre className="text-sm text-slate-800 whitespace-pre-wrap font-sans leading-relaxed">
                  {summaryGemini}
                </pre>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpenSummary(false)}>Cerrar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    );
  }

  // Ya hubo reunión pero aún no se importó la transcripción
  const fechaReunion = reunion.fecha_hora ? new Date(reunion.fecha_hora) : null;
  const yaPasó = fechaReunion && fechaReunion < new Date();
  if (!yaPasó) return null;

  return (
    <div className="mt-1.5 pt-1.5 border-t border-slate-100" data-testid={`transcript-panel-${lead.id}`}>
      <button
        onClick={importarTranscript}
        disabled={importing}
        className="w-full text-[10px] text-indigo-600 hover:text-indigo-800 inline-flex items-center justify-center gap-1 py-1 rounded hover:bg-indigo-50 disabled:opacity-50"
        data-testid={`import-transcript-btn-${lead.id}`}
      >
        {importing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
        {importing ? 'Buscando en Drive…' : 'Importar transcripción Meet'}
      </button>
    </div>
  );
};

const ForzarConfirmacionDialog = ({ lead, onClose, onUpdated }) => {
  const reunion = lead.reunion_socios || {};
  const asistentes = reunion.asistentes_internos || [];
  const [target, setTarget] = useState('cliente'); // 'cliente' | email
  const [estado, setEstado] = useState('confirmado');
  const [razon, setRazon] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/leads/${lead.id}/reunion-socios/forzar-confirmacion`, {
        target, estado, razon,
      });
      toast.success('Estado actualizado manualmente');
      if (onUpdated) onUpdated();
      onClose();
    } catch (e) {
      toast.error(formatErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='max-w-md' data-testid='forzar-confirmacion-dialog'>
        <DialogHeader>
          <DialogTitle>Forzar Confirmación Manual</DialogTitle>
        </DialogHeader>
        <div className='space-y-4'>
          <p className='text-sm text-slate-600'>
            Usa esta función cuando el aviso de asistencia se realizó por una vía externa
            (WhatsApp, llamada telefónica, etc.).
          </p>

          <div>
            <Label className='mb-1 block'>¿A quién aplicar?</Label>
            <select
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className='w-full border border-slate-300 rounded h-9 px-2 text-sm'
              data-testid='forzar-target'
            >
              <option value='cliente'>Cliente: {reunion.cliente_nombre || reunion.cliente_email}</option>
              {asistentes.map(a => (
                <option key={a.email} value={a.email}>
                  {a.name} ({a.cargo})
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label className='mb-1 block'>Nuevo estado</Label>
            <div className='flex gap-2'>
              <button
                onClick={() => setEstado('confirmado')}
                className={`flex-1 h-9 rounded text-sm font-medium ${
                  estado === 'confirmado' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700'
                }`}
                data-testid='forzar-estado-confirmado'
              >Confirmar</button>
              <button
                onClick={() => setEstado('cancelado')}
                className={`flex-1 h-9 rounded text-sm font-medium ${
                  estado === 'cancelado' ? 'bg-rose-600 text-white' : 'bg-slate-100 text-slate-700'
                }`}
                data-testid='forzar-estado-cancelado'
              >Cancelar</button>
            </div>
          </div>

          <div>
            <Label className='mb-1 block'>Razón (opcional)</Label>
            <Textarea
              rows={2}
              value={razon}
              onChange={(e) => setRazon(e.target.value)}
              placeholder='Ej: Confirmó vía WhatsApp'
              data-testid='forzar-razon'
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} className='bg-indigo-600 hover:bg-indigo-700' data-testid='forzar-submit'>
            {saving ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// HistorialAuditDialog — Línea de tiempo de eventos del lead (auditoría)
// ============================================================================
const EVENT_META = {
  reunion_agendada:               { icon: '📅', label: 'Reunión agendada',           color: 'text-indigo-600' },
  cliente_accion_publica:         { icon: '👤', label: 'Cliente actuó vía email',     color: 'text-emerald-600' },
  cliente_estado_gcal_sync:       { icon: '🔄', label: 'Cliente respondió en GCal',   color: 'text-emerald-600' },
  asistente_estado_cambiado:      { icon: '👥', label: 'Asistente cambió estado',     color: 'text-blue-600' },
  asistente_estado_gcal_sync:     { icon: '🔄', label: 'Asistente respondió en GCal', color: 'text-blue-600' },
  forzar_confirmacion_manual:     { icon: '✋', label: 'Confirmación forzada',         color: 'text-amber-600' },
  reunion_cancelada_total:        { icon: '✕', label: 'Reunión cancelada',           color: 'text-rose-600' },
  clonada_a_cerebro:              { icon: '🧠', label: 'Clonada al Cerebro de Tareas', color: 'text-purple-600' },
  invitaciones_reenviadas:        { icon: '📧', label: 'Invitaciones re-enviadas',    color: 'text-slate-600' },
  recordatorio_0900_enviado:      { icon: '⏰', label: 'Recordatorio 09:00 enviado',   color: 'text-cyan-600' },
  alerta_0930_disparada:          { icon: '🚨', label: 'Alerta 09:30 disparada',      color: 'text-rose-700' },
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('es-CL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const HistorialAuditDialog = ({ lead, onClose }) => {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/leads/${lead.id}/reunion-socios/audit`)
      .then(r => setItems(r.data.items || []))
      .catch(() => toast.error('No se pudo cargar el historial'))
      .finally(() => setLoading(false));
  }, [lead.id]);

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='max-w-xl max-h-[80vh]' data-testid='historial-audit-dialog'>
        <DialogHeader>
          <DialogTitle>Historial de la Reunión</DialogTitle>
        </DialogHeader>

        <div className='overflow-y-auto pr-2 -mr-2' style={{ maxHeight: '60vh' }}>
          {loading ? (
            <p className='text-sm text-slate-500 text-center py-8'>Cargando...</p>
          ) : !items?.length ? (
            <p className='text-sm text-slate-500 text-center py-8'>
              Sin eventos registrados todavía.
            </p>
          ) : (
            <ol className='relative border-l-2 border-slate-200 ml-2 space-y-3 pt-2'>
              {items.map((ev, idx) => {
                const meta = EVENT_META[ev.evento] || { icon: '•', label: ev.evento, color: 'text-slate-500' };
                return (
                  <li key={ev.id || idx} className='ml-4' data-testid={`audit-item-${idx}`}>
                    <span className={`absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full bg-white border-2 border-slate-200 ${meta.color}`}>
                      <span className='text-xs'>{meta.icon}</span>
                    </span>
                    <div className='bg-slate-50 rounded-lg p-2.5 border border-slate-100'>
                      <div className='flex items-baseline justify-between gap-2'>
                        <p className={`text-sm font-medium ${meta.color}`}>{meta.label}</p>
                        <p className='text-[10px] text-slate-400 font-mono whitespace-nowrap'>{fmtDate(ev.created_at)}</p>
                      </div>
                      <p className='text-xs text-slate-500 mt-0.5'>
                        Actor: <span className='font-medium'>{ev.actor}</span>
                        {ev.actor_email ? ` · ${ev.actor_email}` : ''}
                      </p>
                      {ev.detalle && Object.keys(ev.detalle).length > 0 && (
                        <div className='mt-1.5 text-[11px] text-slate-600 space-y-0.5'>
                          {Object.entries(ev.detalle).slice(0, 6).map(([k, v]) => (
                            <p key={k}>
                              <span className='font-medium text-slate-700'>{k}:</span>{' '}
                              <span className='font-mono'>
                                {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                              </span>
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onClose} data-testid='historial-cerrar'>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReunionSemaforo;